import { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import chartData from '../../data/chartData.json'
import {
  chartColors,
  barDefaults,
  mergeOptions,
  withOpacity,
  formatCurrency
} from '../../theme/chartTheme'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

// Calculate standard deviation
function calcStdDev(arr) {
  if (arr.length < 2) return 0
  const mean = arr.reduce((a, b) => a + b, 0) / arr.length
  const squaredDiffs = arr.map(x => Math.pow(x - mean, 2))
  const variance = squaredDiffs.reduce((a, b) => a + b, 0) / (arr.length - 1)
  return Math.sqrt(variance)
}

function IndustryComparison({ data: filterData, onBarClick }) {
  // Compute industry stats from filtered data or use static data
  const sorted = useMemo(() => {
    if (filterData && filterData.length > 0) {
      const byIndustry = {}

      filterData.forEach(study => {
        if (!study.industry || !study.efficiency_pct) return

        const industry = study.industry.toLowerCase().replace(/ /g, '_')

        if (!byIndustry[industry]) {
          byIndustry[industry] = {
            efficiencies: [],
            savings: []
          }
        }

        byIndustry[industry].efficiencies.push(study.efficiency_pct)
        if (study.annual_savings) {
          byIndustry[industry].savings.push(study.annual_savings)
        }
      })

      // Convert and sort with stdDev
      return Object.entries(byIndustry)
        .map(([industry, data]) => {
          const mean = data.efficiencies.reduce((a, b) => a + b, 0) / data.efficiencies.length
          const stdDev = calcStdDev(data.efficiencies)
          return {
            key: industry,
            data: {
              average_improvement_pct: mean,
              std_dev: stdDev,
              case_count: data.efficiencies.length,
              average_annual_savings_usd: data.savings.length > 0
                ? data.savings.reduce((a, b) => a + b, 0) / data.savings.length
                : null
            }
          }
        })
        .sort((a, b) => b.data.case_count - a.data.case_count)
        .slice(0, 8)
        .map(item => [item.key, item.data])
    }

    // Use static data
    return Object.entries(chartData.by_industry)
      .sort((a, b) => b[1].case_count - a[1].case_count)
      .slice(0, 8)
  }, [filterData])

  const labels = sorted.map(([key]) =>
    key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  )

  const avgImprovement = sorted.map(([, data]) => data.average_improvement_pct)

  // Calculate error bar data (mean - stdDev to mean + stdDev) as floating bars for horizontal chart
  const errorBars = sorted.map(([, data]) => {
    const mean = data.average_improvement_pct
    const stdDev = data.std_dev || 0
    return [Math.max(0, mean - stdDev), mean + stdDev]
  })

  // Use categorical colors from theme
  const colors = chartColors.categorical

  const data = {
    labels,
    datasets: [
      {
        label: '± 1σ Range',
        data: errorBars,
        backgroundColor: colors.map(c => withOpacity(c, 0.25)),
        borderColor: colors.map(c => withOpacity(c, 0.5)),
        borderWidth: 1,
        borderRadius: 3,
        barPercentage: 0.3,
        order: 2
      },
      {
        label: 'Mean Efficiency Improvement %',
        data: avgImprovement,
        backgroundColor: colors,
        hoverBackgroundColor: colors.map(c => withOpacity(c, 0.8)),
        borderWidth: 0,
        borderRadius: barDefaults.borderRadius,
        barPercentage: 0.6,
        order: 1
      }
    ]
  }

  const options = mergeOptions({
    indexAxis: 'y',
    onClick: (event, elements) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index
        const [industryKey] = sorted[index]
        onBarClick('industry', industryKey)
      }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default'
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 16,
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11,
            weight: 500
          },
          color: chartColors.textSecondary,
          filter: (item) => item.text !== '± 1σ Range'
        }
      },
      tooltip: {
        callbacks: {
          title: (context) => context[0].label,
          afterLabel: (context) => {
            const [, industryData] = sorted[context.dataIndex]
            const stdDev = industryData.std_dev || 0
            return [
              '',
              `Mean: ${industryData.average_improvement_pct.toFixed(1)}% ± ${stdDev.toFixed(1)}%`,
              `Studies: n = ${industryData.case_count}`,
              `Avg Savings: ${industryData.average_annual_savings_usd ? formatCurrency(industryData.average_annual_savings_usd) : 'N/A'}`
            ]
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 50,
        title: {
          display: true,
          text: 'Improvement % (± 1σ)',
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 12,
            weight: 600
          },
          color: chartColors.textSecondary
        },
        grid: {
          color: chartColors.grid
        },
        ticks: {
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11
          },
          color: chartColors.textSecondary
        }
      },
      y: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11,
            weight: 500
          },
          color: chartColors.text
        }
      }
    }
  })

  return <Bar data={data} options={options} />
}

export default IndustryComparison
